import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import ConfigurableServiceTable from './ConfigurableServiceTable';
import { getStatusColorClasses, getStatusDisplayName } from '../../utils/statusUtils';
import EnhancedSearchInput from '../common/EnhancedSearchInput';
import { SearchUtils, useDebouncedCallback } from '../../hooks/useDebounced';

interface ServiceListProps {
  loading: boolean;
  onTicketSelect: (ticket: any) => void;
  onRefresh: () => void;
  lastUpdated?: Date | null;
  autoUpdating?: boolean;
  savedFilters?: {
    statusFilter: string;
    priorityFilter: string;
    searchTerm: string;
    sortBy: string;
    sortOrder: string;
  };
  onFiltersChange?: (filters: {
    statusFilter?: string;
    priorityFilter?: string;
    searchTerm?: string;
    sortBy?: string;
    sortOrder?: string;
  }) => void;
}

const ServiceList: React.FC<ServiceListProps> = ({
  loading,
  onTicketSelect,
  onRefresh,
  lastUpdated,
  autoUpdating = false,
  savedFilters,
  onFiltersChange
}) => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState(savedFilters?.searchTerm || '');
  const [statusFilter, setStatusFilter] = useState(savedFilters?.statusFilter || 'all');
  const [priorityFilter, setPriorityFilter] = useState(savedFilters?.priorityFilter || 'all');
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [agents, setAgents] = useState<any[]>([]);
  const [agentPresence, setAgentPresence] = useState<Map<string, boolean>>(new Map());
  const [reassignTicketId, setReassignTicketId] = useState<string | null>(null);
  const [reassigning, setReassigning] = useState(false);
  const [deletingTicketId, setDeletingTicketId] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 50
  });

  // Update current time every 10 seconds to refresh relative times
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  // Update local state when savedFilters prop changes
  useEffect(() => {
    if (savedFilters) {
      setSearchTerm(savedFilters.searchTerm || '');
      setStatusFilter(savedFilters.statusFilter || 'all');
      setPriorityFilter(savedFilters.priorityFilter || 'all');
    }
  }, [savedFilters]);

  // Load tickets with service workflows
  const loadTicketsWithServices = useCallback(async (page = 1) => {
    setLoadingTickets(true);
    try {
      const filters = {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        priority: priorityFilter !== 'all' ? priorityFilter : undefined,
        search: searchTerm || undefined,
        page,
        limit: pagination.itemsPerPage
      };

      const result = await apiService.getTicketsWithServices(filters);
      setTickets(result.tickets);
      setPagination(result.pagination);
    } catch (error) {
      console.error('Error loading tickets with services:', error);
    } finally {
      setLoadingTickets(false);
    }
  }, [searchTerm, statusFilter, priorityFilter, pagination.itemsPerPage]);

  // Load tickets on mount and when filters change
  useEffect(() => {
    loadTicketsWithServices();
  }, [loadTicketsWithServices]);

  // Load agents for reassign functionality
  useEffect(() => {
    const loadAgents = async () => {
      if (user?.role === 'agent' || user?.userType === 'agent') {
        try {
          const fetchedAgents = await apiService.getAgents();
          setAgents(fetchedAgents);
        } catch (error) {
          console.error('Error loading agents:', error);
        }
      }
    };

    loadAgents();
    
    // Listen for agent status changes
    const handleAgentStatusChange = (data: { agentId: string; isOnline: boolean; lastSeen: string }) => {
      setAgentPresence(prev => {
        const updated = new Map(prev);
        updated.set(data.agentId, data.isOnline);
        return updated;
      });
      
      setAgents(prev => prev.map(agent => 
        agent.id === data.agentId 
          ? { ...agent, isOnline: data.isOnline, lastSeen: data.lastSeen }
          : agent
      ));
    };

    // Listen for bulk agent status updates
    const handleAgentsOnlineStatusUpdate = (data: { onlineAgents: Array<{ agentId: string; isOnline: boolean; lastSeen: string; agentName: string }> }) => {
      setAgentPresence(prev => {
        const updated = new Map(prev);
        
        prev.forEach((_, agentId) => {
          updated.set(agentId, false);
        });
        
        data.onlineAgents.forEach(agent => {
          updated.set(agent.agentId, agent.isOnline);
        });
        
        return updated;
      });
      
      setAgents(prev => prev.map(agent => {
        const onlineAgent = data.onlineAgents.find(oa => oa.agentId === agent.id);
        return onlineAgent 
          ? { ...agent, isOnline: onlineAgent.isOnline, lastSeen: onlineAgent.lastSeen }
          : { ...agent, isOnline: false };
      }));
    };

    // Import socket service dynamically to avoid circular dependencies
    import('../../services/socket').then(({ socketService }) => {
      socketService.on('agent_status_changed', handleAgentStatusChange);
      socketService.on('agents_online_status_update', handleAgentsOnlineStatusUpdate);
    });

    return () => {
      import('../../services/socket').then(({ socketService }) => {
        socketService.off('agent_status_changed', handleAgentStatusChange);
        socketService.off('agents_online_status_update', handleAgentsOnlineStatusUpdate);
      });
    };
  }, [user]);

  // Debounced filter change for preferences saving
  const debouncedFiltersChange = useDebouncedCallback(
    (filters: any) => {
      if (onFiltersChange) {
        onFiltersChange(filters);
      }
    },
    500, // Debounce preference saving
    [onFiltersChange]
  );

  // Filter change handlers that save preferences
  const handleSearchTermChange = useCallback((value: string) => {
    setSearchTerm(value);
    debouncedFiltersChange({ searchTerm: value });
  }, [debouncedFiltersChange]);

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    if (onFiltersChange) {
      onFiltersChange({ statusFilter: value });
    }
  };

  const handlePriorityFilterChange = (value: string) => {
    setPriorityFilter(value);
    if (onFiltersChange) {
      onFiltersChange({ priorityFilter: value });
    }
  };

  const filteredTickets = useMemo(() => {
    return tickets.filter(ticket => {
      // Use enhanced search that supports quoted terms
      const searchFields = [
        ticket.title,
        ticket.description,
        ticket.customerName,
        ticket.customerCompany,
        ticket.customer?.name
      ].filter(Boolean);
      
      const matchesSearch = !searchTerm || searchFields.some(field => 
        SearchUtils.matchesSearch(field, searchTerm)
      );
      
      const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
      const matchesPriority = priorityFilter === 'all' || ticket.priority === priorityFilter;
      
      return matchesSearch && matchesStatus && matchesPriority;
    });
  }, [tickets, searchTerm, statusFilter, priorityFilter]);

  const getStatusColor = (status: string) => {
    return getStatusColorClasses(status as any);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-red-600 dark:text-red-400';
      case 'medium':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'low':
        return 'text-green-600 dark:text-green-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateString: string) => {
    const now = currentTime;
    const messageTime = new Date(dateString).getTime();
    const diffMs = now - messageTime;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) {
      return 'Just now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return new Date(messageTime).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const formatStatus = (status: string) => {
    if (status === 'new') {
      return 'New - Not Assigned';
    }
    return getStatusDisplayName(status as any);
  };

  const formatLastUpdated = () => {
    if (!lastUpdated) return '';
    
    const now = currentTime;
    const diffMs = now - lastUpdated.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    
    if (diffSeconds < 60) {
      return `Updated ${diffSeconds}s ago`;
    } else if (diffMinutes < 60) {
      return `Updated ${diffMinutes}m ago`;
    } else {
      return `Updated at ${lastUpdated.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      })}`;
    }
  };

  const getLastCustomerMessage = (ticket: any) => {
    if ((ticket as any).lastCustomerMessageAt) {
      return {
        id: 'db-timestamp',
        createdAt: (ticket as any).lastCustomerMessageAt,
        content: 'Customer message',
        sender: { userType: 'customer' }
      };
    }
    
    if (!ticket.messages || ticket.messages.length === 0) return null;
    
    const customerMessages = ticket.messages
      .filter((msg: any) => {
        const sender = msg.sender;
        const senderUserType = sender?.userType;
        
        if (senderUserType === 'customer') {
          return true;
        }
        
        if (!sender || (!msg.userId && !msg.senderId)) {
          return true;
        }
        
        if ((ticket as any).isAnonymous && senderUserType !== 'agent' && senderUserType !== 'ai') {
          return true;
        }
        
        if (sender && !senderUserType && senderUserType !== 'agent' && senderUserType !== 'ai') {
          return true;
        }
        
        return false;
      })
      .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    
    return customerMessages.length > 0 ? customerMessages[0] : null;
  };

  const getLastAgentMessage = (ticket: any) => {
    if ((ticket as any).lastAgentMessageAt) {
      return {
        id: 'db-timestamp',
        createdAt: (ticket as any).lastAgentMessageAt,
        content: 'Agent message',
        sender: { userType: 'agent' }
      };
    }
    
    if (!ticket.messages || ticket.messages.length === 0) return null;
    
    const agentMessages = ticket.messages
      .filter((msg: any) => msg.sender?.userType === 'agent' || msg.sender?.userType === 'ai')
      .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    
    return agentMessages.length > 0 ? agentMessages[0] : null;
  };

  const handleDeleteTicket = async (ticketId: string) => {
    if (!window.confirm('Are you sure you want to delete this ticket? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingTicketId(ticketId);
      await apiService.deleteTicket(ticketId);
      loadTicketsWithServices(); // Refresh the ticket list
    } catch (error) {
      console.error('Error deleting ticket:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Failed to delete ticket: ${errorMessage}`);
    } finally {
      setDeletingTicketId(null);
    }
  };

  const handleReassignTicket = async (ticketId: string, newAgentId: string) => {
    try {
      setReassigning(true);
      
      // Get current ticket to check previous assignment
      const currentTicket = tickets.find(t => t.id === ticketId);
      
      await apiService.reassignTicket(ticketId, newAgentId);
      setReassignTicketId(null);
      
      // Emit custom event for immediate tab bar refresh
      if (newAgentId === user?.id) {
        // User assigned ticket to themselves
        console.log('🚀 ServiceList: Emitting ticket-claimed event for self-assignment');
        window.dispatchEvent(new CustomEvent('ticket-claimed', {
          detail: { ticketId: ticketId, agentId: user?.id }
        }));
      } else if (currentTicket?.agentId === user?.id && newAgentId !== user?.id) {
        // User unassigned ticket from themselves
        console.log('🚀 ServiceList: Emitting ticket-unclaimed event for self-unassignment');
        window.dispatchEvent(new CustomEvent('ticket-unclaimed', {
          detail: { ticketId: ticketId, agentId: user?.id }
        }));
      }
      
      loadTicketsWithServices(); // Refresh the ticket list
    } catch (error) {
      console.error('Error reassigning ticket:', error);
      alert('Failed to reassign ticket. Please try again.');
    } finally {
      setReassigning(false);
    }
  };

  const getRowStyle = (ticketId: string) => {
    return {};
  };

  if (loading || loadingTickets) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
            <div className="h-16 bg-gray-100 dark:bg-gray-700 rounded-t-lg mb-2"></div>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between mb-6">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:truncate">
            Service Tickets
          </h2>
          <div className="flex items-center space-x-4 mt-1">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {filteredTickets.length} of {tickets.length} tickets with service workflows
            </p>
            {lastUpdated && (
              <p className="text-xs text-green-600 dark:text-green-400 flex items-center">
                <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {formatLastUpdated()}
              </p>
            )}
            {autoUpdating && (
              <p className="text-xs text-blue-600 dark:text-blue-400 flex items-center">
                <svg className="animate-spin h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Auto-updating...
              </p>
            )}
          </div>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
          <button
            onClick={onRefresh}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          >
            <svg className="-ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Search tickets
            </label>
            <EnhancedSearchInput
              value={searchTerm}
              onChange={handleSearchTermChange}
              placeholder="Search by title, description, customer name, or company... (use &quot;quotes&quot; for exact matches)"
              showSearchTips={true}
              debounceMs={300}
              className="w-full"
            />
          </div>
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Status
            </label>
            <select
              id="status"
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="in_progress">In Progress</option>
              <option value="service">Service</option>
              <option value="closed-resolved">Resolved</option>
              <option value="closed-not-resolved">Not Resolved</option>
            </select>
          </div>
          <div>
            <label htmlFor="priority" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Priority
            </label>
            <select
              id="priority"
              value={priorityFilter}
              onChange={(e) => handlePriorityFilterChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Priorities</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>
      </div>

      {/* Service Tickets Table */}
      {filteredTickets.length === 0 ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No service tickets found</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {searchTerm || statusFilter !== 'all' || priorityFilter !== 'all'
              ? 'Try adjusting your search or filter criteria.'
              : 'No tickets with service workflows have been created yet.'}
          </p>
        </div>
      ) : (
        <ConfigurableServiceTable
          tickets={filteredTickets}
          onTicketSelect={onTicketSelect}
          onDeleteTicket={handleDeleteTicket}
          onReassignTicket={handleReassignTicket}
          getStatusColor={getStatusColor}
          getPriorityColor={getPriorityColor}
          formatDate={formatDate}
          formatRelativeTime={formatRelativeTime}
          getLastCustomerMessage={getLastCustomerMessage}
          getLastAgentMessage={getLastAgentMessage}
          formatStatus={formatStatus}
          getRowStyle={getRowStyle}
          agents={agents}
          agentPresence={agentPresence}
          reassignTicketId={reassignTicketId}
          setReassignTicketId={setReassignTicketId}
          reassigning={reassigning}
          deletingTicketId={deletingTicketId}
          exportFilters={{
            searchTerm,
            statusFilter,
            priorityFilter,
            currentView: 'service-tickets'
          }}
        />
      )}
    </div>
  );
};

export default ServiceList;
